import { z } from 'zod';
import { BetroffeneBuchungSchema, GrundSchema } from './api.js';
import { PersonenNameSchema } from './name.js';
import {
  BuchungSchema,
  EinstellungenSchema,
  KalenderAntwortSchema,
  KalenderStatusSchema,
  ModulSchema,
  SchulungSchema,
  StornoGrundSchema,
  TeilnehmerSchema,
  TerminFormatSchema,
  TerminSchema,
} from './typen.js';

/**
 * Request-/Response-Formate der Admin-API (`/api/verwaltung/*`, plan/Stufe-08). Nur für die App-Rolle Admin; die Prüfung
 * erfolgt in jedem Endpunkt serverseitig.
 */

const isoZeitpunkt = z.iso.datetime({ offset: true });

// ---------- Stammdaten und Einstellungen ----------

export const AdminModulSchema = ModulSchema.extend({
  /** Es gibt aktive Buchungen in der Schulung des Moduls: Reihenfolge gesperrt (F14). */
  reihenfolgeGesperrt: z.boolean(),
});
export type AdminModul = z.infer<typeof AdminModulSchema>;

export const StammdatenResponseSchema = z.object({
  schulungen: z.array(SchulungSchema),
  module: z.array(AdminModulSchema),
  einstellungen: EinstellungenSchema,
});
export type StammdatenResponse = z.infer<typeof StammdatenResponseSchema>;

export const SchulungAendernRequestSchema = z.object({
  anzeigename: z.string().trim().min(1).max(100),
  standardObergrenze: z.number().int().positive().max(500),
});
export type SchulungAendernRequest = z.infer<typeof SchulungAendernRequestSchema>;

const modulFelder = {
  schluessel: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9-]+$/, 'Nur Kleinbuchstaben, Ziffern und Bindestrich'),
  titel: z.string().trim().min(1).max(200),
  beschreibung: z.string().trim().max(2000).optional(),
  reihenfolge: z.number().int().positive(),
  dauerMinuten: z
    .number()
    .int()
    .positive()
    .max(24 * 60),
  istCoaching: z.boolean(),
};
export const ModulAnlegenRequestSchema = z.object({
  schulungId: z.number().int().positive(),
  ...modulFelder,
});
export type ModulAnlegenRequest = z.infer<typeof ModulAnlegenRequestSchema>;
export const ModulAendernRequestSchema = z.object(modulFelder);
export type ModulAendernRequest = z.infer<typeof ModulAendernRequestSchema>;

export const EinstellungenRequestSchema = EinstellungenSchema.extend({
  ansprechpartner: z.string().trim().min(1).max(1000),
});

// ---------- Nachfassliste (F-22, F-23) ----------

export const NachfassEintragSchema = z.object({
  kennung: z.string(),
  name: PersonenNameSchema,
  lernpfad: z.string(),
  copilot: z.boolean(),
  pflichtAnzahl: z.number(),
  offen: z.array(
    z.object({ modulId: z.number(), titel: z.string(), kalenderAbgelehnt: z.boolean() }),
  ),
  /** Gebuchte Termine, deren Einladung noch unbeantwortet ist (F-23). */
  unbeantwortet: z.number(),
});
export type NachfassEintrag = z.infer<typeof NachfassEintragSchema>;
export const NachfasslisteResponseSchema = z.array(NachfassEintragSchema);

// ---------- Teilnehmer (F-21, F-24) ----------

export const TeilnehmerZeileSchema = z.object({
  kennung: z.string(),
  name: PersonenNameSchema,
  lernpfad: z.string(),
  copilot: z.boolean(),
  aktiv: z.boolean(),
  pflichtAnzahl: z.number(),
  gebucht: z.number(),
  kalenderAbgelehnt: z.number(),
  unbeantwortet: z.number(),
});
export type TeilnehmerZeile = z.infer<typeof TeilnehmerZeileSchema>;
export const TeilnehmerListeResponseSchema = z.array(TeilnehmerZeileSchema);

export const AdminTerminSchema = TerminSchema.extend({
  modulTitel: z.string(),
  schulungName: z.string(),
});
export type AdminTermin = z.infer<typeof AdminTerminSchema>;

export const TeilnehmerDetailResponseSchema = z.object({
  teilnehmer: TeilnehmerSchema.extend({ name: PersonenNameSchema }),
  module: z.array(
    z.object({
      modul: ModulSchema,
      schulungName: z.string(),
      status: z.enum(['offen', 'gebucht']),
      kalenderAbgelehnt: z.boolean(),
      buchung: z
        .object({
          id: z.number(),
          termin: AdminTerminSchema,
          gebuchtAm: z.string(),
          gebuchtVon: z.string(),
          kalenderStatus: KalenderStatusSchema,
          kalenderAntwort: KalenderAntwortSchema,
        })
        .optional(),
    }),
  ),
  /** Stornierte Buchungen, neueste zuerst. */
  verlauf: z.array(
    z.object({
      buchungId: z.number(),
      modulTitel: z.string(),
      beginn: z.string(),
      storniertAm: z.string().optional(),
      storniertVon: z.string().optional(),
      stornoGrund: StornoGrundSchema.optional(),
    }),
  ),
});
export type TeilnehmerDetailResponse = z.infer<typeof TeilnehmerDetailResponseSchema>;

export const UebergehenSchema = z.object({
  ueberschneidung: z.boolean().optional(),
});

export const AdminBuchenRequestSchema = z.object({
  kennung: z.string().min(3),
  terminId: z.number().int().positive(),
  uebergehen: UebergehenSchema.default({}),
});
export type AdminBuchenRequest = z.input<typeof AdminBuchenRequestSchema>;

export const AdminUmbuchenRequestSchema = z.object({
  terminId: z.number().int().positive(),
  uebergehen: UebergehenSchema.default({}),
});
export type AdminUmbuchenRequest = z.input<typeof AdminUmbuchenRequestSchema>;

export const KennungRequestSchema = z.object({ kennung: z.string().min(3) });

export const EntfernenVorschauSchema = z.object({ buchungen: z.array(BetroffeneBuchungSchema) });

/** Teilnehmer in einen anderen Lernpfad umziehen und/oder die Copilot-Berechtigung ändern (R-01, R-02). */
export const TeilnehmerAendernRequestSchema = z.object({
  kennung: z.string().min(3),
  /** Schlüssel der Lernpfad-Schulung, z. B. `office` oder `power`. */
  lernpfad: z.string().trim().min(1),
  copilot: z.boolean(),
});
export type TeilnehmerAendernRequest = z.infer<typeof TeilnehmerAendernRequestSchema>;

/** Einen Nachzügler in die Teilnehmerliste aufnehmen (R-08); der Name kommt aus dem Verzeichnis. */
export const TeilnehmerHinzufuegenRequestSchema = z.object({
  /** Anmeldekennung (UPN) des Mitarbeiters. */
  kennung: z.string().trim().min(3),
  /** Schlüssel der Lernpfad-Schulung, z. B. `office` oder `power`. */
  lernpfad: z.string().trim().min(1),
  copilot: z.boolean(),
});
export type TeilnehmerHinzufuegenRequest = z.infer<typeof TeilnehmerHinzufuegenRequestSchema>;

export const TeilnehmerHinzugefuegtSchema = z.object({
  kennung: z.string(),
  name: PersonenNameSchema,
  /** Stand schon einmal auf der Liste und wurde entfernt. */
  wiederaufgenommen: z.boolean(),
});

// ---------- Import ----------

/** Inhalt der CSV-Datei als Text (die Oberfläche liest die Datei und erkennt die Kodierung). */
export const CsvRequestSchema = z.object({ csv: z.string().min(1).max(2_000_000) });

const ImportFehlerSchema = z.object({ zeile: z.number(), code: z.string(), meldung: z.string() });

export const TeilnehmerImportVorschauSchema = z.object({
  fehler: z.array(ImportFehlerSchema),
  neu: z.array(
    z.object({
      kennung: z.string(),
      name: PersonenNameSchema,
      lernpfad: z.string(),
      copilot: z.boolean(),
    }),
  ),
  geaendert: z.array(
    z.object({
      kennung: z.string(),
      name: PersonenNameSchema,
      /** Lesbare Änderungen, z. B. „Lernpfad: office → power“. */
      aenderungen: z.array(z.string()),
      reaktiviert: z.boolean(),
    }),
  ),
  unveraendert: z.number(),
  stornierungen: z.array(
    z.object({
      buchungId: z.number(),
      kennung: z.string(),
      name: PersonenNameSchema,
      modulTitel: z.string(),
      beginn: z.string(),
      grund: z.enum(['Lernpfadwechsel', 'CopilotEntzogen']),
    }),
  ),
  uebernehmbar: z.boolean(),
});
export type TeilnehmerImportVorschau = z.infer<typeof TeilnehmerImportVorschauSchema>;

export const TeilnehmerImportErgebnisSchema = z.object({
  neu: z.number(),
  geaendert: z.number(),
  storniert: z.array(z.number()),
});

export const TerminImportVorschauSchema = z.object({
  /** Der Termin-Import ist nur zur Erstbefüllung möglich (F-A4). */
  termineVorhanden: z.boolean(),
  termine: z.array(
    z.object({
      zeile: z.number(),
      modulTitel: z.string(),
      schulungName: z.string(),
      beginn: z.string(),
      ende: z.string(),
      obergrenze: z.number(),
      trainerText: z.string().optional(),
    }),
  ),
  fehler: z.array(z.object({ zeile: z.number(), meldung: z.string() })),
  uebernehmbar: z.boolean(),
});
export type TerminImportVorschau = z.infer<typeof TerminImportVorschauSchema>;
export const TerminImportErgebnisSchema = z.object({ angelegt: z.number() });

// ---------- Termine (F-20, F-26, F-27) ----------

export const TerminListeResponseSchema = z.array(
  AdminTerminSchema.extend({ unbeantwortet: z.number() }),
);

export const TerminDetailResponseSchema = z.object({
  termin: AdminTerminSchema,
  teilnehmer: z.array(
    z.object({
      buchungId: z.number(),
      kennung: z.string(),
      name: PersonenNameSchema,
      gebuchtAm: z.string(),
      gebuchtVon: z.string(),
      kalenderStatus: KalenderStatusSchema,
      kalenderAntwort: KalenderAntwortSchema,
    }),
  ),
  /** Teilnehmer, die die Einladung zu diesem Termin in Outlook abgelehnt haben (F-23). */
  abgelehnt: z.array(
    z.object({ kennung: z.string(), name: PersonenNameSchema, storniertAm: z.string().optional() }),
  ),
  trainerExportAktiv: z.boolean(),
});
export type TerminDetailResponse = z.infer<typeof TerminDetailResponseSchema>;

const terminFelder = {
  beginn: isoZeitpunkt,
  ende: isoZeitpunkt,
  format: TerminFormatSchema,
  ort: z.string().trim().max(200).optional(),
  /** Leerer Text löscht den Link beim Ändern. */
  teamsLink: z.union([z.url().max(2000), z.literal('')]).optional(),
  /** Ausnahme für diesen Termin. Fehlt der Wert oder ist er `null`, gilt die Einstellung der Schulung. */
  eigeneObergrenze: z.number().int().positive().max(500).nullable().optional(),
  trainerText: z.string().trim().max(200).optional(),
  hinweise: z.string().trim().max(2000).optional(),
};
export const TerminAnlegenRequestSchema = z.object({
  modulId: z.number().int().positive(),
  ...terminFelder,
});
export type TerminAnlegenRequest = z.infer<typeof TerminAnlegenRequestSchema>;

export const TerminAenderungSchema = z.object(terminFelder).partial();
export type TerminAenderungRequest = z.infer<typeof TerminAenderungSchema>;

export const TerminAendernRequestSchema = z.object({
  aenderung: TerminAenderungSchema,
  konflikteBestaetigt: z.boolean().default(false),
});

export const TerminKonfliktSchema = z.object({
  kennung: z.string(),
  name: PersonenNameSchema,
  gruende: z.array(GrundSchema),
});
export const TerminAenderungsVorschauSchema = z.object({
  konflikte: z.array(TerminKonfliktSchema),
  aktualisierungen: z.number(),
});
export type TerminAenderungsVorschau = z.infer<typeof TerminAenderungsVorschauSchema>;

export const TerminAbsageVorschauSchema = z.object({ buchungen: z.number() });

export const StorniertErgebnisSchema = z.object({ storniert: z.array(z.number()) });

export const TrainerExportSchema = z.object({ dateiname: z.string(), inhalt: z.string() });

// ---------- System ----------

export const SystemResponseSchema = z.object({
  datenquelle: z.enum(['mock', 'graph']),
  /** Erreichbarkeit der SharePoint-Listen, des Verzeichnisses und (ab Stufe 6) des Kampagnen-Postfachs. */
  pruefungen: z.array(z.object({ name: z.string(), ok: z.boolean(), meldung: z.string() })),
  kalender: z.object({
    ausstehend: z.number(),
    versendet: z.number(),
    fehler: z.number(),
    abgesagt: z.number(),
  }),
  /** Buchungen, deren Einladung bzw. Absage fehlgeschlagen ist (N-06). */
  fehlgeschlagen: z.array(
    z.object({
      buchungId: z.number(),
      kennung: z.string(),
      name: PersonenNameSchema,
      modulTitel: z.string(),
      beginn: z.string(),
      status: BuchungSchema.shape.status,
    }),
  ),
  protokoll: z.array(
    z.object({
      zeitpunkt: z.string(),
      kennung: z.string(),
      aktion: z.string(),
      objektTyp: z.string(),
      objektId: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});
export type SystemResponse = z.infer<typeof SystemResponseSchema>;

export const AbgleichBerichtSchema = z.object({
  zaehlerKorrigiert: z.number(),
  umbuchungenAbgeschlossen: z.number(),
  umbuchungenVerworfen: z.number(),
  /** Nur mit Outlook-Anbindung (Stufe 6). */
  kalender: z
    .object({ nachgeholt: z.number(), fehler: z.number(), antwortenGeaendert: z.number() })
    .optional(),
});

/**
 * Stand des Teams-Testtermins (Systemseite): Ein Termin mit Teams-Besprechung im Kampagnen-Postfach, den die
 * Anwendung mit ihren eigenen Rechten anlegt, um Teilnehmer ergänzt oder entfernt und löscht. Entscheidend ist
 * `joinUrl`; fehlt sie oder scheitert ein Aufruf, steht der Grund in `fehler` bzw. in `istOnlineMeeting`/`anbieter`.
 */
export const TeamsTestterminErgebnisSchema = z.object({
  postfach: z.string(),
  /** Der Aufruf bei Graph ist gelungen (auch ein Termin ohne Teams-Link). */
  ok: z.boolean(),
  /** Meldung von Graph, wenn der Aufruf gescheitert ist. */
  fehler: z.string().optional(),
  eventId: z.string().optional(),
  istOnlineMeeting: z.boolean().optional(),
  anbieter: z.string().optional(),
  joinUrl: z.string().optional(),
  /** Eingeladene mit Antwort-Status (nie Antworttexte, S-06). */
  teilnehmer: z.array(z.object({ adresse: z.string(), antwort: KalenderAntwortSchema })).optional(),
  /** Der Termin wurde gelöscht; Graph schickt den Eingeladenen dabei die Absage. */
  geloescht: z.boolean().optional(),
});
export type TeamsTestterminErgebnis = z.infer<typeof TeamsTestterminErgebnisSchema>;

/** Gewünschte Teilnehmerliste des Testtermins (komplett, wie Graph sie ersetzt). */
export const TeamsTestteilnehmerRequestSchema = z.object({
  teilnehmer: z.array(z.string().trim().toLowerCase().pipe(z.email())).max(20),
});
